/**
 * Snapping in the place dialog (spec §17 rule 6): a map click within SNAP_RADIUS_PX screen pixels of a vertex of a
 * visible design layer (a point feature, or a line or polygon vertex) lands on that vertex. Pure: the dialog turns the
 * features the map already holds into vertices and screen points; this picks the nearest.
 */

export const SNAP_RADIUS_PX = 12;
/** The most vertices one snap looks at; past this the rest are ignored (a huge polygon near the cursor) */
export const MAX_SNAP_VERTICES = 20000;

export interface ScreenVertex<T = unknown> {
  /** Screen position, px */
  x: number;
  y: number;
  /** Whatever the caller needs back (the map point) */
  ref: T;
}

type Coord = number[];

export interface VertexBox {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

/**
 * Every vertex of a geometry, in its own coordinates: ArcGIS JSON (`{x, y}`, `points`, `paths`, `rings`) or GeoJSON
 * (Point, MultiPoint, LineString, MultiLineString, Polygon, MultiPolygon, GeometryCollection). With `box`, only the
 * vertices inside it count, and they are kept before the `limit` applies: a long line's near vertex is never cut off by
 * the cap because of thousands of far ones.
 */
export function geometryVertices(geometry: unknown, limit = MAX_SNAP_VERTICES, box?: VertexBox | null): Coord[] {
  const out: Coord[] = [];
  const push = (c: unknown) => {
    if (out.length >= limit || !Array.isArray(c) || c.length < 2 || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) {
      return;
    }
    const x = c[0] as number;
    const y = c[1] as number;
    if (box && (x < box.xmin || x > box.xmax || y < box.ymin || y > box.ymax)) {
      return;
    }
    out.push([x, y]);
  };
  const walk = (g: unknown, depth: number): void => {
    if (!g || typeof g !== 'object' || depth > 4 || out.length >= limit) {
      return;
    }
    const o = g as Record<string, unknown>;
    if (Number.isFinite(o.x) && Number.isFinite(o.y)) {
      push([o.x, o.y]);
      return;
    }
    for (const key of ['points']) {
      (Array.isArray(o[key]) ? (o[key] as unknown[]) : []).forEach(push);
    }
    for (const key of ['paths', 'rings']) {
      (Array.isArray(o[key]) ? (o[key] as unknown[][]) : []).forEach(part => (Array.isArray(part) ? part : []).forEach(push));
    }
    const coords = o.coordinates;
    switch (o.type) {
      case 'Point':
        push(coords);
        break;
      case 'MultiPoint':
      case 'LineString':
        (Array.isArray(coords) ? coords : []).forEach(push);
        break;
      case 'MultiLineString':
      case 'Polygon':
        (Array.isArray(coords) ? coords : []).forEach(part => (Array.isArray(part) ? part : []).forEach(push));
        break;
      case 'MultiPolygon':
        (Array.isArray(coords) ? coords : []).forEach(poly =>
          (Array.isArray(poly) ? poly : []).forEach(part => (Array.isArray(part) ? part : []).forEach(push)));
        break;
      case 'GeometryCollection':
        (Array.isArray(o.geometries) ? o.geometries : []).forEach(child => walk(child, depth + 1));
        break;
    }
  };
  walk(geometry, 0);
  return out;
}

/** The candidate nearest the click within `radius` screen px (the first one on a tie), or null */
export function nearestVertex<T>(click: { x: number; y: number }, candidates: ScreenVertex<T>[], radius = SNAP_RADIUS_PX): ScreenVertex<T> | null {
  let best: ScreenVertex<T> | null = null;
  let bestD = radius * radius;
  for (const c of candidates) {
    if (!Number.isFinite(c?.x) || !Number.isFinite(c?.y)) {
      continue;
    }
    const d = (c.x - click.x) ** 2 + (c.y - click.y) ** 2;
    if (d <= bestD && (!best || d < bestD)) {
      best = c;
      bestD = d;
    }
  }
  return best;
}
